import React, { useRef, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../styles/Clients.css";
import HomeBranches from "./HomeBranches";
import ClientStats from "./ClientStats";
import NewlyAddedClients from "./NewlyAddedClients";
import CompanyProfile from "./CompanyProfile";  

const LaxmiClientsSection = () => {
  const titleRef = useRef(null);
  const introRef = useRef(null);

  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("animate-in");
      } else {
        entry.target.classList.remove("animate-in");
      }
    });
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentTitleRef = titleRef.current;
    const currentIntroRef = introRef.current;

    if (currentTitleRef) {
      observer.observe(currentTitleRef);
    }
    if (currentIntroRef) {
      observer.observe(currentIntroRef);
    }

    return () => {
      if (currentTitleRef) {
        observer.unobserve(currentTitleRef);
      }
      if (currentIntroRef) {
        observer.unobserve(currentIntroRef);
      }
    };
  }, [handleIntersect]);

  return (
    <>
   <Helmet>
       <title>Our Clients Across India | Laxmi Hospitality Services</title>
       <meta
       name="description"
       content="Discover our prestigious clients across 5 states: Maharashtra, Gujarat, Daman, Andhra Pradesh & Telangana. Serving 25+ industrial locations with 14,550+ meals daily since 2009."
       />
       <meta name="robots" content="index, follow" />
       <meta name="viewport" content="width=device-width, initial-scale=1.0" />
       <link rel="canonical" href="https://laxmihospitalityservices.in/clients" />
     
       <meta property="og:type"        content="website" />
       <meta property="og:url"         content="https://laxmihospitalityservices.in/clients" />
       <meta property="og:title"       content="Clients Across India | Laxmi Hospitality Services" />
       <meta property="og:description" content="Discover our prestigious clients across 5 states: Maharashtra, Gujarat, Daman, Andhra Pradesh & Telangana. 25+ industrial locations, 14,550+ meals daily." />
       <meta property="og:site_name"   content="Laxmi Hospitality Services" />
       <meta property="og:locale"      content="en_IN" />
            
       {/* -------- COLLECTION PAGE STRUCTURED DATA -------- */}
       <script type="application/ld+json">
       {`
         {
           "@context": "https://schema.org",
           "@type": "CollectionPage",
           "@id": "https://laxmihospitalityservices.in/clients",
           "name": "Our Clients Across India",
           "description": "Industrial catering clients across Maharashtra, Daman, Gujarat, Andhra Pradesh and Telangana - serving 25+ locations with 17,500+ meals daily.",
           "url": "https://laxmihospitalityservices.in/clients",
           "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
           "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
           "sameAs": [
             "https://www.linkedin.com/company/laxmi-hospitality-services/",
             "https://www.instagram.com/laxmi_hospitality_services/"
           ],
           "inLanguage": "en-IN",
           "hasPart": [
             {
               "@type": "WebPage",
               "name": "Maharashtra Clients",
               "url": "https://laxmihospitalityservices.in/clients/maharashtra"
             },
             {
               "@type": "WebPage",
               "name": "Daman Clients",
               "url": "https://laxmihospitalityservices.in/clients/daman"
             },
              {
               "@type": "WebPage",
               "name": "Gujarat Clients", 
               "url": "https://laxmihospitalityservices.in/clients/gujarat"
             },
             {
               "@type": "WebPage",
               "name": "Andhra Pradesh Clients",
               "url": "https://laxmihospitalityservices.in/clients/andhra-pradesh"
             },
             {
               "@type": "WebPage",
               "name": "Telangana Clients",
               "url": "https://laxmihospitalityservices.in/clients/telangana"
             }
           ]
         }
      `}
      </script>

      {/* -------- PAGE-SPECIFIC BREADCRUMB -------- */}
      <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "@id": "https://laxmihospitalityservices.in/#breadcrumb",
          "name": "Website Breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://laxmihospitalityservices.in/" },
            { "@type": "ListItem", "position": 2, "name": "Clients", "item": "https://laxmihospitalityservices.in/clients" }
          ]
        }
      `}
      </script>
</Helmet>

    
    <div className="clients-page-wrapper">
      <div className="clients-page-hero-section">
        {" "}
        <img
          src={process.env.PUBLIC_URL + "/images/clients-hero-image.jpg"}
          alt="Our Clients Banner"
          className="clients-page-hero-image"
          title="Clients Hero Image"
        />
        <div className="clients-page-hero-overlay">
          {" "}
          <h1 className="clients-page-hero-title">Our Clients</h1>{" "}
          <p className="clients-page-breadcrumb">
            {" "}
            <Link to="/" className="clients-page-breadcrumb-link">
              Home
            </Link>{" "}
            / Clients
          </p>
        </div>
      </div>
      
      <NewlyAddedClients />
      <HomeBranches/>
     <ClientStats/>
     <CompanyProfile />
    </div>
    </>
  );
};

export default LaxmiClientsSection;
